import type { LucideIcon } from 'lucide-react';
import {
  ClipboardList,
  Coins,
  Cpu,
  Download,
  FileInput,
  Flag,
  GraduationCap,
  LayoutDashboard,
  Lightbulb,
  LineChart,
  MessageSquare,
  Palette,
  Radio,
  Settings,
  Ticket,
  UserCog,
  UserRound,
  Users,
} from 'lucide-react';
import { CONCEPT_ICONS } from '../../utils/concept-icons';

export interface AdminNavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
}

export interface AdminBreadcrumbItem {
  labelKey: string;
  to?: string;
}

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { to: '/admin', labelKey: 'admin_panel', icon: LayoutDashboard, end: true },
  { to: '/admin/users', labelKey: 'users', icon: Users },
  { to: '/admin/all-users', labelKey: 'all_users', icon: UserRound },
  { to: '/admin/suggestions', labelKey: 'suggestions', icon: Lightbulb },
  { to: '/admin/style-notes', labelKey: 'admin_style_notes_nav', icon: Palette },
  { to: '/admin/llm-calls', labelKey: 'llm_calls', icon: Cpu },
  { to: '/admin/credits-activity', labelKey: 'credits_activity', icon: Coins },
  { to: '/admin/feature-tracking', labelKey: 'feature_usage', icon: LineChart },
  { to: '/admin/user-feedback', labelKey: 'user_feedback', icon: MessageSquare },
  { to: '/admin/invitation-codes', labelKey: 'invitation_codes', icon: Ticket },
  { to: '/admin/feature-flags', labelKey: 'feature_flags', icon: Flag },
  { to: '/admin/waiting-list', labelKey: 'waiting_list', icon: ClipboardList },
  { to: '/admin/site-settings', labelKey: 'site_settings', icon: Settings },
  { to: '/admin/impersonation-actions', labelKey: 'impersonation_actions', icon: UserCog },
  { to: '/admin/observability-events', labelKey: 'observability_events', icon: Radio },
  { to: '/admin/onboarding', labelKey: 'admin_onboarding_title', icon: GraduationCap },
  { to: '/admin/exports', labelKey: 'exports', icon: Download },
  { to: '/admin/word-imports', labelKey: 'word_imports', icon: FileInput },
  { to: '/admin/characters-analyses', labelKey: 'characters_analyses', icon: CONCEPT_ICONS.character },
  { to: '/admin/chapter-analyses', labelKey: 'chapter_analyses', icon: CONCEPT_ICONS.manual_chapter },
];

export const isAdminNavActive = (pathname: string, item: AdminNavItem): boolean => {
  if (item.end) {
    return pathname === item.to;
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
};

export const getAdminBreadcrumbItems = (pathname: string): AdminBreadcrumbItem[] => {
  if (pathname === '/admin') {
    return [{ labelKey: 'admin_panel' }];
  }

  const adminRoot: AdminBreadcrumbItem = { labelKey: 'admin_panel', to: '/admin' };

  if (/^\/admin\/users\/[^/]+$/.test(pathname)) {
    return [
      adminRoot,
      { labelKey: 'users', to: '/admin/users' },
      { labelKey: 'user_details' },
    ];
  }

  const match = ADMIN_NAV_ITEMS.find(
    (item) => item.to !== '/admin' && (pathname === item.to || pathname.startsWith(`${item.to}/`))
  );
  if (match) {
    return [adminRoot, { labelKey: match.labelKey }];
  }

  return [adminRoot];
};
